/**
 * Insurer-aware helpers for the patient "Confirm Details" insurance section.
 *
 * Pure, side-effect-free functions extracted from PatientConfirmDetails so the
 * insurer-specific label resolution, OCR-candidate routing, and submission
 * payload shape can be unit-tested without mounting the React component.
 *
 * Terminology is the generated insurerTerminology.json (exported from
 * insurer_registry — the same source the OCR miner uses). Everything degrades
 * gracefully to generic labels / generic routing when no insurer matches.
 */

import terminology from "./insurerTerminology.json";

// Generic fallback labels when no insurer matches (today's copy).
export const GENERIC_LABELS: { plan: string; member: string; division: string } = {
  plan: "Plan / Policy / Group # *",
  member: "Member ID / Certificate #",
  division: "Division / Group #",
};

/**
 * Match a free-text provider string to a terminology insurer entry.
 * Tolerant of name/alias/short_name variance and substring matches (the
 * provider field is patient-typed / OCR-detected, not a canonical key).
 * Returns { name, entry } or null.
 */
export function matchInsurer(providerText: unknown): { name: string; entry: any } | null {
  const insurers: Record<string, any> = (terminology && (terminology as any).insurers) || {};
  if (!providerText || !String(providerText).trim()) return null;
  const lc = String(providerText).trim().toLowerCase();

  // Exact canonical-name match first.
  for (const [name, entry] of Object.entries(insurers)) {
    if (name.trim().toLowerCase() === lc) return { name, entry };
  }
  // Alias / short_name exact.
  for (const [name, entry] of Object.entries(insurers)) {
    const alts = [entry.short_name]
      .concat(entry.aliases || [])
      .filter(Boolean)
      .map((a) => String(a).trim().toLowerCase());
    if (alts.includes(lc)) return { name, entry };
  }
  // Substring — the typed text contains a canonical name or alias (e.g.
  // "Manulife Financial Group" contains "manulife").
  for (const [name, entry] of Object.entries(insurers)) {
    const needles = [name, entry.short_name]
      .concat(entry.aliases || [])
      .filter(Boolean)
      .map((a) => String(a).trim().toLowerCase());
    if (needles.some((n) => n.length >= 3 && (lc.includes(n) || n.includes(lc)))) {
      return { name, entry };
    }
  }
  return null;
}

function conceptLabel(entry: any, concept: string, fallback: string): string {
  if (!entry) return fallback;
  const spec = (entry.id_terminology || []).find((s: any) => s.concept === concept);
  return (spec && spec.label) || fallback;
}

/**
 * Resolve the printed labels the patient should see for a given provider.
 * Returns { plan, member, division, declaresDivision, matched }.
 * When matched, the plan/member labels become the insurer's printed labels
 * (e.g. Manulife → "Certificate number"); a "*" is appended to the plan label
 * (plan number is the required identifier). Division label only matters when
 * the insurer declares one.
 */
interface InsuranceLabels {
  plan: string;
  member: string;
  division: string;
  declaresDivision: boolean;
  matched: boolean;
}

export function insuranceLabels(providerText: unknown): InsuranceLabels {
  const match = matchInsurer(providerText);
  if (!match) {
    return { ...GENERIC_LABELS, declaresDivision: false, matched: false };
  }
  const entry = match.entry;
  const declaresDivision = !!entry.declares_division;
  return {
    plan: `${conceptLabel(entry, "plan_id", "Plan / Policy #")} *`,
    member: conceptLabel(entry, "member_id", "Member ID / Certificate #"),
    division: conceptLabel(entry, "division_id", GENERIC_LABELS.division),
    declaresDivision,
    matched: true,
  };
}

/**
 * Route OCR candidates to the confirm-details value keys BY LEVEL — this kills
 * the old group_id→plan_number collapse. Rules:
 *   - level "person"  → insurance_id  (member)
 *   - level "plan" + concept "division_id" → division_id when the division
 *     input is shown (insurer declares it), otherwise plan_number
 *   - level "plan" (any other plan concept) → plan_number
 *   - insurance_provider → insurance_provider
 * Legacy candidates without a level fall back to the pre-canonical mapping
 * (group_id→plan_number) so old OCR payloads still route.
 *
 * Never overwrites a value the patient already typed (empty-check on current
 * values) and applies a 0.5 confidence threshold. Returns a patch object of
 * key→value to merge into state.
 */
export function routeCandidates(
  candidates: any[] | null | undefined,
  currentValues: Record<string, unknown> | null | undefined,
  opts: { showDivision?: boolean; threshold?: number } = {},
): Record<string, unknown> {
  const showDivision = !!opts.showDivision;
  const threshold = opts.threshold == null ? 0.5 : opts.threshold;
  const patch: Record<string, unknown> = {};

  // Highest-confidence candidate wins its slot: sort desc so a clean carrier-
  // pass value (e.g. "12345", 0.85) beats a noisy generic first-pass value
  // (e.g. "contract no 12345", 0.77) that routes to the same key.
  const ordered = [...(candidates || [])].sort(
    (a, b) => (b.confidence || 0) - (a.confidence || 0)
  );

  for (const c of ordered) {
    if (!c || c.confidence == null || c.confidence < threshold) continue;

    let key: any;
    if (c.field === "insurance_provider") {
      key = "insurance_provider";
    } else if (c.level === "person") {
      key = "insurance_id";
    } else if (c.level === "plan") {
      if (c.concept === "division_id" && showDivision) key = "division_id";
      else key = "plan_number";
    } else {
      // Legacy candidate (no level): preserve the old field mapping.
      key = c.field === "group_id" ? "plan_number" : c.field;
      if (c.concept === "division_id" && showDivision) key = "division_id";
    }

    const allowed =
      key === "insurance_provider" ||
      key === "plan_number" ||
      key === "insurance_id" ||
      key === "division_id";
    if (!allowed) continue;

    // Never overwrite a typed value or an earlier-in-this-batch assignment.
    if (String((currentValues || {})[key] || "").trim()) continue;
    if (patch[key] !== undefined) continue;
    patch[key] = c.value;
  }
  return patch;
}

/** The onboarding wizard's own key names for the three insurance boxes it shows.
 *  The index signature is not laziness: callers pass the WHOLE wizard profile,
 *  which carries thirty other keys, and without it TypeScript's weak-type check
 *  rejects an all-optional target as having nothing in common with it. */
export interface WizardInsuranceValues {
  insuranceCarrier?: unknown;
  memberId?: unknown;
  planNumber?: unknown;
  [key: string]: unknown;
}

/**
 * The same OCR routing, for the ONBOARDING WIZARD's key names.
 *
 * The wizard is where a new patient first types their insurance, so it is
 * where the card photo earns the most — but it names its boxes
 * `insuranceCarrier` / `memberId` / `planNumber`, while `routeCandidates`
 * speaks the confirm-details names (`insurance_provider` / `insurance_id` /
 * `plan_number`). This translates in both directions and delegates the real
 * decisions — level routing, the 0.5 confidence threshold, highest-confidence
 * wins, never overwrite what the patient already typed — so the two surfaces
 * cannot drift into disagreeing about what an OCR candidate means.
 *
 * `showDivision` is deliberately false: the wizard shows no division input, so
 * a division candidate routes to the plan box exactly as it does on a
 * confirm-details form for an insurer that declares no division.
 */
export function routeCandidatesToWizard(
  candidates: any[] | null | undefined,
  profile: WizardInsuranceValues | null | undefined,
): Partial<Record<"insuranceCarrier" | "memberId" | "planNumber", string>> {
  const p = profile || {};
  const patch = routeCandidates(
    candidates,
    {
      insurance_provider: p.insuranceCarrier,
      insurance_id: p.memberId,
      plan_number: p.planNumber,
    },
    { showDivision: false },
  );

  const out: Partial<Record<"insuranceCarrier" | "memberId" | "planNumber", string>> = {};
  if (patch.insurance_provider !== undefined) out.insuranceCarrier = String(patch.insurance_provider);
  if (patch.insurance_id !== undefined) out.memberId = String(patch.insurance_id);
  if (patch.plan_number !== undefined) out.planNumber = String(patch.plan_number);
  return out;
}

/**
 * Build the canonical insurance sub-doc for submission. ADDITIVE over the
 * existing shape — keeps provider/plan_number/member_id (+ any pre-existing
 * sub-doc keys) exactly, and adds plan_id (= plan_number value), division_id,
 * and a plans[] array with the primary patient-reported plan. An old bridge
 * ignores the new keys; a new bridge reads plans[0].
 */
export function buildInsuranceSubDoc(
  prevInsurance: Record<string, unknown> | null | undefined,
  values: Record<string, unknown>,
) {
  const provider = String(values.insurance_provider || "").trim();
  const planNumber = String(values.plan_number || "").trim();
  const memberId = String(values.insurance_id || "").trim();
  const divisionId = String(values.division_id || "").trim();

  return {
    ...(prevInsurance || {}),
    // Existing keys — unchanged.
    provider,
    plan_number: planNumber,
    member_id: memberId,
    // Canonical additive keys.
    plan_id: planNumber,
    division_id: divisionId,
    plans: [
      {
        rank: "primary",
        carrier: provider,
        plan_id: planNumber,
        member_id: memberId,
        division_id: divisionId,
        source: "patient_reported",
      },
    ],
  };
}
